import { useState } from 'react';
import { ComboBox, ComboBoxFilterChangeEvent, ComboBoxProps } from '@progress/kendo-react-dropdowns';
import { FieldRenderProps } from '@progress/kendo-react-form';
import { FieldWrapper } from 'components';
import { DisputeEventTypes } from 'enums';
import { useResolutionReasons } from 'state';
import { ResolutionReason } from 'types';

export type ResolutionReasonComboBoxProps = {
    type: DisputeEventTypes
} & FieldRenderProps;

/**
 * ResolutionReasonComboBox component allows the user to select the ResolutionReason for completing
 * or denying a dispute.
 * 
 * @param type - Filters the available ResolutionReason options displayed in the ComboBox
 * @param props - Additional properties passed to the ComboBox
 * @returns Rendered component
 */
export const ResolutionReasonComboBox = ({ type, ...props }: ResolutionReasonComboBoxProps) => {
    const reasons = useResolutionReasons(type).map(reason => {
        const { description } = reason;
        return {
            ...reason,
            text: description.toLowerCase()
        };
    });
    const [data, setData] = useState<ResolutionReason[]>(reasons);

    const filterChange = ({ filter: { value } }: ComboBoxFilterChangeEvent) => {
        const search = value?.toLowerCase();
        setData(search?.length > 1 ? reasons.filter(({ text }) => text.includes(search)) : reasons);
    };

    return (
        <FieldWrapper label="Reason" required {...props}>
            <ComboBox
                id="reason"
                aria-required="true"
                ariaLabelledBy="reason_label"
                data={data}
                dataItemKey="resolutionReasonId"
                textField="description"
                placeholder="Select a reason"
                filterable
                onFilterChange={filterChange}
                {...props as ComboBoxProps}
            />
        </FieldWrapper>
    );
};

export const resolutionReasonValidator = (value: string) => value ? '' : 'Required. Please select a reason';
